/**
 * What this device can actually run, asked before anything is downloaded.
 *
 * WHY THIS EXISTS. A visitor should never spend 2.2 GB of their data plan to
 * discover their GPU cannot execute the model. Every failure mode below was
 * either measured on this project or is a documented WebGPU floor, and all of
 * them are knowable from the adapter in about a millisecond.
 *
 * Three things can stop a model here, and they fail in very different places:
 *
 * 1. KERNEL COVERAGE. Measured 2026-07-26: Gemma 4 E2B QAT downloads all
 *    2.29 GB and then fails at session creation with
 *      GatherBlockQuantized: bits_ == 4 || bits_ == 8 was false
 *    Its embedding table is 2-bit block-quantized and ONNX Runtime Web's
 *    WebGPU kernel for quantized embedding lookup implements only 4 and 8
 *    bits. This is NOT a general 2-bit limit: Ternary Bonsai runs fine at
 *    q2f16 because its low-bit weights live in matmuls, which do have a 2-bit
 *    WebGPU path. Nothing in the ORT operator documentation mentions the bit
 *    widths, so a measured blocklist is the only honest source.
 *
 * 2. MISSING shader-f16. Every dtype here ending in f16 needs that feature.
 *    It is optional in the WebGPU spec, and absent on plenty of older and
 *    mobile hardware. This is the failure most likely to hit real visitors,
 *    because it has nothing to do with how much memory they have.
 *
 * 3. BUFFER BINDING CEILING. maxStorageBufferBindingSize is guaranteed to be
 *    only 128 MiB by the spec. This machine reports 2,147,483,644, roughly
 *    2 GB, which is why large models load here and may not elsewhere. It is
 *    the limit you cannot discover by testing on your own machine, so it is
 *    reported as a warning rather than assumed away.
 */

import type { ModelEntry } from "./catalog";

export type GpuCaps = {
  supported: boolean;
  /** Present only when WebGPU is available. */
  hasF16: boolean;
  hasSubgroups: boolean;
  maxBufferSize: number;
  maxStorageBufferBindingSize: number;
  /** Free-form vendor/architecture, when the browser chooses to report it. */
  adapter: string;
  reason?: string;
};

const UNSUPPORTED: GpuCaps = {
  supported: false,
  hasF16: false,
  hasSubgroups: false,
  maxBufferSize: 0,
  maxStorageBufferBindingSize: 0,
  adapter: "",
  reason: "This browser does not expose WebGPU.",
};

let cached: Promise<GpuCaps> | null = null;

export function probeGpu(): Promise<GpuCaps> {
  cached ??= (async () => {
    const gpu = (navigator as Navigator & { gpu?: GPU }).gpu;
    if (!gpu) return UNSUPPORTED;
    let adapter: GPUAdapter | null = null;
    try {
      adapter = await gpu.requestAdapter();
    } catch {
      adapter = null;
    }
    if (!adapter) {
      return { ...UNSUPPORTED, reason: "WebGPU is present but no adapter was granted." };
    }
    const info = (adapter as GPUAdapter & { info?: GPUAdapterInfo }).info;
    return {
      supported: true,
      hasF16: adapter.features.has("shader-f16"),
      hasSubgroups: adapter.features.has("subgroups"),
      maxBufferSize: Number(adapter.limits.maxBufferSize),
      maxStorageBufferBindingSize: Number(adapter.limits.maxStorageBufferBindingSize),
      adapter: [info?.vendor, info?.architecture].filter(Boolean).join("/"),
    };
  })();
  return cached;
}

export type RunVerdict = {
  /** False means do not offer the download at all. */
  runnable: boolean;
  /** Set when it will probably run but something is worth saying first. */
  warning?: string;
  reason?: string;
};

/** True when any component of this entry is stored in a half-precision format. */
export function needsF16(entry: ModelEntry): boolean {
  const dts = typeof entry.dtype === "string" ? [entry.dtype] : Object.values(entry.dtype);
  return dts.some((d) => d.endsWith("f16"));
}

export function canRun(entry: ModelEntry, caps: GpuCaps): RunVerdict {
  if (!caps.supported) return { runnable: false, reason: caps.reason };

  if (entry.webgpuBlocked) {
    return { runnable: false, reason: entry.webgpuBlocked };
  }

  if (needsF16(entry) && !caps.hasF16) {
    return {
      runnable: false,
      reason:
        "This model is stored in half precision and your GPU does not report the " +
        "shader-f16 feature, so it cannot be executed here.",
    };
  }

  // A model's weights arrive as one or more external data files, and no single
  // tensor can be larger than the file holding it. Comparing the largest file
  // against the binding ceiling is therefore a conservative test: it can warn
  // when everything would in fact have been fine, but it will not stay quiet
  // when a tensor genuinely cannot be bound. Warn rather than block, because
  // the false-positive direction should not cost anyone a working model.
  if (entry.largestFileBytes && entry.largestFileBytes > caps.maxStorageBufferBindingSize) {
    const gb = (n: number) => (n / 1e9).toFixed(2) + " GB";
    return {
      runnable: true,
      warning:
        `One of this model's weight files is ${gb(entry.largestFileBytes)} and your GPU ` +
        `binds at most ${gb(caps.maxStorageBufferBindingSize)} at once. It may fail to ` +
        `start after downloading.`,
    };
  }

  return { runnable: true };
}
